import React from "react";
import PillDelta from "./PillDelta";
import Sparkline from "./Sparkline";
import useCountUp from "../lib/useCountUp";

/**
 * ModuleHero — compact hero strip used at the top of Sales / Inventory /
 * Procurement / HR pages. Shares the same visual language as the Overview
 * hero (navy gradient with soft blobs) but only 140-160px tall.
 *
 * Props:
 *  - eyebrow, title, subtitle
 *  - primary:   { label, value: number | string, countFormatter?, delta?, decimals? }
 *  - secondaries: [{ label, value, delta?, accent? }]
 *  - spark:     number[] optional trend series to render inside the hero
 *  - accent:    "teal" | "violet" | "amber" | "red" — highlights the blobs
 *  - testId
 */

const ACCENT_BLOB = {
  teal:   { a: "rgba(0,180,216,0.55)", b: "rgba(124,111,240,0.55)" },
  violet: { a: "rgba(124,111,240,0.55)", b: "rgba(0,180,216,0.42)" },
  amber:  { a: "rgba(245,166,35,0.42)",  b: "rgba(124,111,240,0.45)" },
  red:    { a: "rgba(240,68,56,0.42)",   b: "rgba(0,180,216,0.42)" },
};

export const ModuleHero = ({
  eyebrow, title, subtitle,
  primary, secondaries = [], spark,
  accent = "teal", testId,
}) => {
  const isNumeric = primary?.value != null && typeof primary.value === "number";
  const animated = useCountUp(isNumeric ? primary.value : 0, {
    duration: 800,
    decimals: primary?.decimals ?? 0,
    startOnMount: isNumeric,
  });
  const displayPrimary = isNumeric
    ? (primary.countFormatter ? primary.countFormatter(animated) : animated)
    : (primary?.value ?? "—");

  const blob = ACCENT_BLOB[accent] || ACCENT_BLOB.teal;

  return (
    <section
      data-testid={testId || "module-hero"}
      className="ang-hero-bg text-white relative overflow-hidden ang-fade-up"
      style={{ borderRadius: 22, boxShadow: "var(--ang-shadow-hero)" }}
    >
      <div
        className="ang-blob absolute pointer-events-none"
        style={{
          top: -70, right: -50, width: 240, height: 240,
          background: `radial-gradient(circle, ${blob.a} 0%, transparent 70%)`,
          filter: "blur(24px)",
        }}
      />
      <div
        className="ang-blob absolute pointer-events-none"
        style={{
          bottom: -80, left: -30, width: 260, height: 260,
          background: `radial-gradient(circle, ${blob.b} 0%, transparent 70%)`,
          filter: "blur(28px)",
          animationDelay: "2.5s",
        }}
      />

      <div className="relative z-10 grid grid-cols-1 md:grid-cols-[1.5fr_1fr] gap-5 items-center px-6 sm:px-7 py-6 sm:py-7">
        {/* LEFT */}
        <div>
          {eyebrow && (
            <div
              className="text-[10px] font-bold uppercase tracking-widest"
              style={{ color: "rgba(255,255,255,0.65)" }}
            >
              {eyebrow}
            </div>
          )}
          <h2
            className="mt-1.5 font-extrabold leading-tight"
            style={{ fontSize: "clamp(22px, 2.4vw, 28px)", letterSpacing: "-0.022em" }}
          >
            {title}
          </h2>
          {subtitle && (
            <p
              className="mt-1 text-[13px] leading-snug max-w-md"
              style={{ color: "rgba(255,255,255,0.68)" }}
            >
              {subtitle}
            </p>
          )}

          <div className="mt-4 flex items-baseline gap-3 flex-wrap">
            {primary?.label && (
              <div
                className="text-[10px] font-bold uppercase tracking-widest"
                style={{ color: "rgba(255,255,255,0.55)" }}
              >
                {primary.label}
              </div>
            )}
            <span
              className="block leading-none font-black tabular-nums"
              style={{ fontSize: "clamp(30px, 3.6vw, 40px)", letterSpacing: "-0.03em" }}
            >
              {displayPrimary}
            </span>
            {primary?.delta != null && <PillDelta value={primary.delta} />}
          </div>
        </div>

        {/* RIGHT — spark + secondary stats */}
        <div className="flex flex-col gap-3 md:items-end">
          {spark && spark.length > 1 && (
            <div className="w-full md:w-[220px] flex md:justify-end">
              <div style={{ background: "rgba(255,255,255,0.06)", borderRadius: 12, padding: 8 }}>
                <Sparkline data={spark} color="#7BDDF5" width={200} height={40} strokeWidth={2} />
              </div>
            </div>
          )}
          {secondaries.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-2 gap-4 md:gap-6 w-full md:justify-items-end">
              {secondaries.map((s, i) => (
                <div key={i} className="min-w-0">
                  <div
                    className="text-[10px] font-bold uppercase tracking-widest truncate"
                    style={{ color: "rgba(255,255,255,0.55)" }}
                  >
                    {s.label}
                  </div>
                  <div className="mt-1 flex items-baseline gap-2">
                    <span
                      className="tabular-nums font-extrabold text-lg"
                      style={{
                        letterSpacing: "-0.02em",
                        color: s.accent === "amber" ? "#FFD98A"
                             : s.accent === "red"   ? "#FF9F94"
                             : s.accent === "green" ? "#88F5B1"
                             : "#FFFFFF",
                      }}
                    >
                      {s.value}
                    </span>
                    {s.delta != null && <PillDelta value={s.delta} />}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default ModuleHero;
